import React, { useEffect, useRef } from 'react';
import { Animated, Platform, StyleProp, ViewStyle } from 'react-native';

export type CollapsableContentOwnProps = {
    isCollapsed: boolean;
    children: React.ReactNode;
    expandedHeight: number;
    style?: StyleProp<ViewStyle>;
};

export type CollapsableContentProps = CollapsableContentOwnProps;

const CollapsableContent: React.FC<CollapsableContentProps> = (props) => {
    const [isAnimating, setIsAnimating] = React.useState(false);
    const animationRef = useRef(new Animated.Value(props.isCollapsed ? 0 : 1));
    const animationRunIdRef = useRef(0);
    useEffect(() => {
        const runId = ++animationRunIdRef.current;
        setIsAnimating(true);
        Animated.timing(animationRef.current, {
            toValue: props.isCollapsed ? 0 : 1,
            duration: 300,
            useNativeDriver: Platform.OS === 'web',
        }).start(() => {
            // Clear the flag whether the animation finished or was interrupted. Interrupted runs
            // report finished: false, so only clearing on completion strands `animating-parent` on
            // this subtree forever. The run id guards the other direction: an interrupted run's
            // callback fires after the run that replaced it has already re-armed the flag, so only
            // the newest run is allowed to clear it.
            if (animationRunIdRef.current !== runId) return;
            setIsAnimating(false);
        });
    }, [props.isCollapsed]);

    return (
        <Animated.View
            style={[
                {
                    height: animationRef.current.interpolate({
                        inputRange: [0, 1],
                        outputRange: [0, props.expandedHeight],
                    }),
                    opacity: animationRef.current,
                    overflow: 'hidden',
                },
                props.style,
            ]}
            testID={isAnimating ? 'animating-parent' : undefined}
        >
            {props.children}
        </Animated.View>
    );
};

export default React.memo(CollapsableContent);
